/* eslint-disable no-nested-ternary */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import AccountCard from "components/features/account/account-card/accountCard";
import {
  Tabs,
  Box,
  Button,
  Grid,
  Modal,
  TextInput,
  Select,
  Space,
  Group,
  rem,
  Text,
} from "@mantine/core";
import {
  IconDownload,
  IconPrinter,
  IconPointFilled,
  IconSearch,
  IconRefresh,
  IconChevronDown,
  IconArrowRight,
  IconFileInvoice,
  IconAlertTriangle,
} from "@tabler/icons-react";
import TableComponent from "components/tables/table/TableComponent";
import accountData from "modules/accounts/account-list/AccountData";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DateInput } from "@mantine/dates";
import DoughnutChart from "components/common/chart/chart";
import { useNavigate, useParams } from "react-router-dom";
import { toCurrency } from "utils/common";
import PaginationInfo from "components/tables/pagination/PaginationInfo";
import PaginationControls from "components/tables/pagination/PaginationControls";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { toggleRowState } from "@/helpers/tableHelpers";
import ContractInformation from "../../account-detail/ContractInformation";

const { accountDetailHistory, accountViewInstallment } = accountData;

const LoanAccountPanelDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openedRow, setOpenedRow] = useState<number | null>(null);
  const [
    isOpenContractInformation,
    { open: openContractInformation, close: closeContractInformation },
  ] = useDisclosure(false);
  const { accountId } = useParams();

  const handleRowToggle = (index: number) => {
    setOpenedRow(toggleRowState(openedRow, index));
  };

  const pieData: any[] = [
    { label: t("PAID"), value: "40.000.000 VND", detail: 4, color: "#2A81D0" },
    {
      label: t("PRINCIPAL_AMOUNT"),
      value: "100.000.000 VND",
      detail: 10,
      color: "#AEDEF9",
    },
  ];

  const loanData = [
    {
      value: "account summary",
      label: t("ACCOUNT_SUMMARY"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
            display: "flex",
          }}
        >
          <Box
            style={{
              flex: "1",
            }}
          >
            <DoughnutChart
              title={t("PAID_AMOUNT")}
              data={pieData}
              size={230}
              thickness={30}
            />
          </Box>
          <Box
            style={{
              flex: "1",
            }}
          >
            <Box
              style={{
                borderBottom: "1px dashed #D8DDE2",
                paddingBottom: "20px",
                marginBottom: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("OUTSTANDING_BALANCE")}</p>
              <p className="text-[#2A81D0] text-[24px]">
                <span className="font-bold">431.209.000</span>
                <span className="text-[14px]">VND</span>
              </p>
            </Box>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",

                color: "#E6492D",
                borderBottom: "1px dashed #D8DDE2",
                paddingBottom: "20px",
                marginBottom: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("STATUS")}</p>
              <Box
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <IconPointFilled />
                <span>{t("OVERDUE")}</span>
              </Box>
            </Box>
            <Box
              style={{
                display: "flex",
              }}
            >
              <Box
                style={{
                  borderRight: "1px dashed #D8DDE2",
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("MATURITY_DATE")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  22/12/2023
                </p>
              </Box>
              <Box
                style={{
                  marginLeft: "20px",
                  flex: 1,
                }}
              >
                <p className="text-[#5E656B]">{t("PAYMENT_FREQUENCY")}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  5.000.000 VND
                </p>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      value: "interest details",
      label: t("INTEREST_DETAILS"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "start",
            }}
          >
            <Box
              style={{
                width: "250px",
                borderRight: "1px dashed #D8DDE2",
              }}
            >
              <p className="text-[#5E656B]">{t("LAST_INTEREST_RATE")}</p>
              <p className="text-[#131618] text-[16px] font-bold">10.5%</p>
            </Box>
            <Box
              style={{
                width: "250px",
                borderRight: "1px dashed #D8DDE2",
                marginLeft: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("CURRENT_INTEREST_RATE")}</p>
              <p className="text-[#131618] text-[16px] font-bold">10 %</p>
            </Box>
            <Box
              style={{
                width: "250px",
                marginLeft: "20px",
              }}
            >
              <p className="text-[#5E656B]">{t("INTEREST_IN_NEXT_PERIOD")}</p>
              <p className="text-[#131618] text-[16px] font-bold">
                1.000.000 VND
              </p>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      value: "account info",
      label: t("ACCOUNT_INFO"),
      panelContent: (
        <Box
          style={{
            padding: "20px",
          }}
        >
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            {[
              {
                label: t("ACCOUNT_NUMBER"),
                value: "LD100632",
                flexGrow: 1,
              },
              {
                label: t("ACCOUNT_NAME"),
                value: "Loan 1",
                flexGrow: 1,
              },
              {
                label: t("OPENING_BRANCH"),
                value: "CN QUAN 2",
                flexGrow: 1,
              },
              {
                label: t("CURRENCY"),
                value: "VND",
                flexGrow: 1,
              },
            ].map((item, index, array) => (
              <Box
                style={{
                  flexGrow: item.flexGrow,
                  textAlign: "start",
                  borderRight:
                    index !== array.length - 1 ? "1px dashed #D8DDE2" : "none",
                  padding: "0 10px",
                  flexBasis: 0,
                }}
              >
                <p className="text-[#5E656B]">{item.label}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {item.value}
                </p>
              </Box>
            ))}
          </Box>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "20px",
            }}
          >
            {[
              {
                label: t("PRODUCT_NAME"),
                value: t("PURCHASE_CAR_LOAN"),
                flexGrow: 1,
              },
              {
                label: t("OPEN_DATE"),
                value: "23/02/2023",
                flexGrow: 1,
              },
              {
                label: t("MATURITY_DATE"),
                value: "23/02/2028",
                flexGrow: 1,
              },
              {
                label: t("PURPOSE"),
                value: "VLD - Xang dau",
                flexGrow: 1,
              },
            ].map((item, index, array) => (
              <Box
                style={{
                  flexGrow: item.flexGrow,
                  textAlign: "start",
                  borderRight:
                    index !== array.length - 1 ? "1px dashed #D8DDE2" : "none",
                  padding: "0 10px",
                  flexBasis: 0,
                }}
              >
                <p className="text-[#5E656B]">{item.label}</p>
                <p className="text-[#131618] text-[16px] font-bold">
                  {item.value}
                </p>
              </Box>
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  const tabsHistory = [
    {
      value: "transaction history",
      label: t("TRANSACTION_HISTORY"),
      panelContent: (
        <Box>
          <TableComponent
            dataSource={{
              columns: [
                { title: t("TRANSACTION_ID") },
                { title: t("TRANSACTION_DATE") },
                { title: t("TRANSACTION_REMARKS"), size: 200 },
                { title: <Text ta="right">{t("DEBIT_AMOUNT")}</Text> },
                { title: <Text ta="right">{t("CREDIT_AMOUNT")}</Text> },
              ],
              data: accountDetailHistory.map((item: any, index: number) => {
                return {
                  transactionId: <Text weight={600}>{item.transactionId}</Text>,
                  transactionDate: item.transactionDate,
                  transactionRemarks: item.transactionRemarks,
                  debitAmount: (
                    <Text c="#F37021" fw={600} ta="right">
                      {toCurrency(item.debitAmount) || "-"}
                    </Text>
                  ),
                  creditAmount: (
                    <Text c="#2A81D0" fw={600} ta="right">
                      {toCurrency(item.creditAmount) || "-"}
                    </Text>
                  ),
                };
              }),
            }}
          />
          <Space h={rem(10)} />
          <Group position="apart" sx={{ padding: "12px 20px" }}>
            <PaginationInfo current={1} total={10} />
            <PaginationControls
              currentPage={1}
              totalPages={10}
              onPageChange={() => {}}
            />
          </Group>
        </Box>
      ),
    },
    {
      value: "view next installment",
      label: t("VIEW_NEXT_INSTALLMENT"),
      panelContent: (
        <Box>
          <TableComponent
            dataSource={{
              columns: [
                { title: "", size: 72 },
                { title: t("DUE_DATE") },
                { title: t("OPENING_LIABILITY_AMOUNT"), size: 200 },
                { title: <Text ta="right">{t("INSTALLMENT_AMOUNT")}</Text> },
                { title: <Text ta="right">{t("PRINCIPAL_AMOUNT")}</Text> },
                { title: <Text ta="right">{t("INTEREST_AMOUNT")}</Text> },
              ],
              data: accountViewInstallment.map((item: any, index: number) => {
                return {
                  collapseButton: (
                    <button
                      type="button"
                      onClick={() => handleRowToggle(index)}
                      aria-expanded={openedRow === index}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        marginLeft: "12px",
                      }}
                    >
                      {openedRow === index ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </button>
                  ),
                  dueDate: item.dueDate,
                  Liability: (
                    <Text fw={600} ta="right">
                      {toCurrency(item.Liability) || "-"}
                    </Text>
                  ),
                  installment: (
                    <Text fw={600} ta="right">
                      {toCurrency(item.installment) || "-"}
                    </Text>
                  ),
                  principal: (
                    <Text fw={600} ta="right">
                      {toCurrency(item.principal) || "-"}
                    </Text>
                  ),
                  interest: (
                    <Text fw={600} ta="right">
                      {toCurrency(item.interest) || "-"}
                    </Text>
                  ),
                  collapse: (
                    <div
                      style={{
                        padding: "0px 16px 16px 16px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Grid gutter={0} align="center">
                        <Grid.Col span={12}>
                          <Box
                            style={{
                              backgroundColor: "white",
                              height: "81px",
                              display: "flex",
                              justifyContent: "start",
                              alignItems: "center",
                            }}
                          >
                            <Box style={{ padding: "16px" }}>
                              <div>{t("CLOSING_LIABILITY_AMOUNT")}</div>
                              {/* Closing Liability Amount = Opening Liability Amount - Principal Amount */}
                              <div className="text-[14px] font-semibold">
                                {toCurrency(
                                  Number(item.Liability.replaceAll(".", "")) -
                                    Number(item.principal.replaceAll(".", ""))
                                ) || "-"}
                              </div>
                            </Box>
                          </Box>
                        </Grid.Col>
                      </Grid>
                    </div>
                  ),
                };
              }),
            }}
            openedRow={openedRow}
          />

          <Space h={rem(10)} />
          <Group position="apart" sx={{ padding: "12px 20px" }}>
            <PaginationInfo current={1} total={10} />
            <PaginationControls
              currentPage={1}
              totalPages={10}
              onPageChange={() => {}}
            />
          </Group>
        </Box>
      ),
    },
  ];

  const downloadFile = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "file.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Text weight={800} size={24}>
        {t("ACCOUNT_DETAILS")}
      </Text>
      <Space h={rem(10)} />
      <AccountCard title="VND-LD100632" description={`${t("LOAN_1")}`} />
      <Box
        style={{
          display: "flex",
          gap: "10px",
        }}
      >
        <Box
          style={{
            flex: "1",
          }}
        >
          <TabsCustomer defaultValue="account summary" tabs={loanData} />
          <TabsHistory
            accountId={accountId}
            defaultValue="transaction history"
            tabs={tabsHistory}
          />
        </Box>
        <Box
          style={{
            width: "309px",
            height: "210px", // Tăng chiều cao để chứa thêm nội dung
            borderRadius: "10px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            background: "#FDFDFD",
            margin: "24px 0px",
          }}
        >
          <Box
            style={{
              fontWeight: 700,
              fontSize: "18px",
              lineHeight: "25.2px",
              letterSpacing: "0%",
              borderBottom: "2px solid #dee2e6",
              padding: "18.5px",
            }}
          >
            {t("QUICK_ACTION")}
          </Box>

          {/* Mục 1: OVERDUE_PAYMENT */}
          <Box
            sx={{
              padding: "11px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <IconFileInvoice stroke={2} color="#2A81D0" />
              <Box>{t("CLOSURE")}</Box>
            </Box>
            <IconArrowRight stroke={2} color="#2A81D0" />
          </Box>

          {/* Mục 2: ADVANCE_PAYMENT */}
          <Box
            sx={{
              padding: "11px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <IconFileInvoice stroke={2} color="#2A81D0" />
              <Box>{t("ADVANCE_PAYMENT")}</Box>
            </Box>
            <IconArrowRight stroke={2} color="#2A81D0" />
          </Box>
        </Box>
      </Box>
      <ContractInformation
        opened={isOpenContractInformation}
        onClose={closeContractInformation}
        fileUrl="/pdf/file.pdf"
        onDowload={() => {
          downloadFile("/pdf/file.pdf");
        }}
      />
    </div>
  );
};

const TabsCustomer = ({ defaultValue, tabs }: any) => {
  return (
    <Tabs
      styles={{
        root: {
          background: "#FDFDFD",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px 0px #638DBF1A",
          margin: "24px 0px",
        },
      }}
      defaultValue={defaultValue}
    >
      <Tabs.List>
        {tabs.map((item: any) => {
          return (
            <Tabs.Tab
              sx={(theme) => ({
                "&[data-active]": {
                  color: "#2A81D0",
                  fontWeight: 600,
                  borderBottom: `2px solid #2A81D0`,
                },
                "&[data-active]:hover": {
                  borderBottom: `2px solid #2A81D0`,
                },
                // width: "150px",
                height: "64px",
              })}
              value={item.value}
            >
              {item.label}
            </Tabs.Tab>
          );
        })}
      </Tabs.List>

      {tabs.map((item: any) => {
        return <Tabs.Panel value={item.value}>{item.panelContent}</Tabs.Panel>;
      })}
    </Tabs>
  );
};

const TabsHistory = ({ defaultValue, tabs, accountId }: any) => {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultValue);

  const today = new Date();
  const maxDays = 180;

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const form = useForm<{
    fromDate: Date | null;
    toDate: Date;
  }>({
    initialValues: {
      fromDate: null,
      toDate: today,
    },
  });

  const handleFromDateChange = (value: Date | null) => {
    const { toDate } = form.values;
    if (!value) return;

    if (value > toDate) {
      showNotification({
        title: t("ERROR"),
        message:
          t("FROM_DATE_CANNOT_BE_AFTER_TO_DATE") ||
          "Ngày bắt đầu không được lớn hơn ngày kết thúc",
        color: "red",
        icon: <IconAlertTriangle size={18} />,
      });
      return;
    }

    form.setFieldValue("fromDate", value);
  };

  const handleToDateChange = (value: Date | null) => {
    if (!value) return;

    form.setFieldValue("toDate", value);

    const { fromDate } = form.values;
    const minFromDate = addDays(value, -maxDays);
    if (fromDate && (fromDate > value || fromDate < minFromDate)) {
      form.setFieldValue("fromDate", null);
    }
  };

  const handleRefreshForm = () => {
    form.reset();
  };
  return (
    <>
      <Tabs
        styles={{
          root: {
            background: "#FDFDFD",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px 0px #638DBF1A",
            margin: "24px 0px",
          },
        }}
        defaultValue={defaultValue}
        value={activeTab}
        onTabChange={setActiveTab}
      >
        <Tabs.List
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          })}
        >
          {tabs.map((item: any) => (
            <Tabs.Tab
              sx={(theme) => ({
                "&[data-active]": {
                  color: "#2A81D0",
                  fontWeight: 600,
                  borderBottom: `2px solid #2A81D0`,
                },
                "&[data-active]:hover": {
                  borderBottom: `2px solid #2A81D0`,
                },
                // width: "150px",
                height: "64px",
              })}
              value={item.value}
            >
              {item.label}
            </Tabs.Tab>
          ))}
          <Box
            sx={(theme) => ({
              marginLeft: "auto",
              marginRight: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            })}
          >
            <Button
              variant="subtle"
              leftIcon={<IconDownload size="0.9rem" />}
              color="#2A81D0"
            >
              {t("DOWNLOAD")}
            </Button>
            <Button
              variant="subtle"
              leftIcon={<IconPrinter size="0.9rem" />}
              color="#2A81D0"
            >
              {t("PRINT")}
            </Button>
            <Box
              style={{
                padding: "4px 6px",
                borderRadius: "8px",
                background: "#E5F4FE",
                display: "flex",
                alignItems: "center",
                marginLeft: "8px",
              }}
              onClick={() => setOpened(true)}
            >
              <IconSearch color="#2A81D0" />
            </Box>
          </Box>
        </Tabs.List>

        {tabs.map((item: any) => {
          return (
            <Tabs.Panel value={item.value}>{item.panelContent}</Tabs.Panel>
          );
        })}
      </Tabs>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton
        fullScreen
        overlayProps={{ opacity: 0.5, blur: 0.5 }}
        transitionProps={{ transition: "slide-right", duration: 300 }}
        styles={{
          header: { borderBottom: "solid 1px #dee2e6" },
          title: {
            fontWeight: 700,
            fontSize: "18px",
            color: "#131618",
          },
          content: {
            width: "594px",
            position: "fixed",
            top: 0,
            right: 0,
            borderRadius: "0 !important",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
          body: {
            flex: 1,
            overflowY: "auto",
            paddingBottom: "80px",
            paddingTop: "12px !important",
          },
        }}
        title={`${
          activeTab === "transaction history"
            ? t("FIND_TRANSACTION")
            : t("FILTER")
        }`}
      >
        <form className="h-full" onSubmit={() => {}}>
          <TextInput
            label={`${t("SOURCE_ACCOUNT")}`}
            placeholder="VND-LD100632"
            mb="sm"
            name="search"
            disabled
          />
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
            mb="sm"
          >
            <DateInput
              label={t("TRANSACTION_FROM_DATE")}
              // placeholder={t("FIND_ACCOUNT") as string}
              valueFormat="DD/MM/YYYY"
              style={{ flex: 1 }}
              minDate={addDays(form.values.toDate, -maxDays)}
              maxDate={form.values.toDate}
              {...form.getInputProps("fromDate")}
              onChange={handleFromDateChange}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            />

            <DateInput
              label={t("TRANSACTION_TO_DATE")}
              placeholder={t("FIND_ACCOUNT") as string}
              valueFormat="DD/MM/YYYY"
              // readOnly
              style={{ flex: 1 }}
              // maxDate={today}
              {...form.getInputProps("toDate")}
              onChange={handleToDateChange}
            />
          </Box>
          {activeTab === "transaction history" &&
            // accountId === "32432324324" && (
            accountId !== "4534534534535" && (
              <Select
                label={t("AMOUNT_TYPE")}
                placeholder={`${t("SELECT_CURRENCY")}`}
                // data={["ALL", "USD", "EUR"]}
                data={[
                  {
                    value: "Debit amount",
                    label: t("DEBIT_AMOUNT") || "Debit amount",
                  },
                  {
                    value: "Credit amount",
                    label: t("CREDIT_AMOUNT") || "Credit amount",
                  },
                  { value: "All", label: t("ALL") || "All" },
                ]}
                defaultValue={t("ALL") || "All"}
                mb="sm"
                name="accountCurrency"
                rightSection={<IconChevronDown size="1rem" />}
              />
            )}
          {activeTab === "transaction history" &&
            // accountId !== "32432324324" && (
            accountId === "4534534534535" && (
              <Box>
                <Select
                  label={t("AMOUNT_TYPE")}
                  placeholder={`${t("SELECT_CURRENCY")}`}
                  data={[
                    {
                      value: "Debit amount",
                      label: t("DEBIT_AMOUNT") || "Debit amount",
                    },
                    {
                      value: "Credit amount",
                      label: t("CREDIT_AMOUNT") || "Credit amount",
                    },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountCurrency"
                  rightSection={<IconChevronDown size="1rem" />}
                />
                <Select
                  label={t("TRANSACTION_CHANNEL")}
                  placeholder={`${t("SELECT_TRANSACTION_CHANNEL")}`}
                  data={[
                    { value: "ib", label: t("IB") || "ib" },
                    { value: "mb", label: t("MB") || "mb" },
                    { value: "branch", label: t("BRANCH") || "Branch" },
                    { value: "others", label: t("OTHERS") || "Others" },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountType"
                  rightSection={<IconChevronDown size="1rem" />}
                />
                <Select
                  label={t("TRANSACTION_TYPE")}
                  placeholder={`${t("SELECT_ACCOUNT_TYPE")}`}
                  data={[
                    { value: "ft", label: "FT" },
                    { value: "tt", label: "TT" },
                    { value: "bill", label: t("BILL") || "Bill" },
                    { value: "atm", label: "ATM" },
                    { value: "All", label: t("ALL") || "All" },
                  ]}
                  defaultValue={t("ALL") || "All"}
                  mb="sm"
                  name="accountType"
                  rightSection={<IconChevronDown size="1rem" />}
                />
              </Box>
            )}
          <Box
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              borderTop: "1px solid #e0e0e0",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              sx={() => ({
                border: "solid 1px #2A81D0",
                borderRadius: "8px",
                padding: "8px",
              })}
              variant="outline"
            >
              <IconRefresh
                size={18}
                color="#2A81D0"
                onClick={handleRefreshForm}
              />
            </Button>
            <Button
              type="submit"
              sx={() => ({
                backgroundColor: "#2A81D0",
                color: "white",
                borderRadius: "8px",
                padding: "8px 10px",
                cursor: "pointer",
                fontWeight: 400,
              })}
            >
              {t("SEARCH")}
            </Button>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default LoanAccountPanelDetail;
